import React, { useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as Addons from "../../utilities/Addons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCircle,
	faDollarSign,
	faTrash,
} from "@fortawesome/free-solid-svg-icons"
import AppBtnHover from "../basics/button/AppBtnHover"
import { useGroups } from "../../hooks/groups"
import Modal from "react-responsive-modal"
import { round100, typeNumber } from "../../utilities/Addons"
import { useAtelierPermissions } from "../../hooks/ateliers"

const AtelierLigneV2 = ({
	atelierLigne,
	index,
	updateAtelierLignes,
	updateAtelierLignesFull,
	removeAtelierLignes,
	focusMenu,
	idname,
	dragAL,
	dragOverAL,
	dropAL,
	disableLigne,
	isSVP,
	lastRef,
	isModele = false,
}) => {
	const refQuantite = useRef(null)
	const refPrixU = useRef(null)
	const refPrixAchat = useRef(null)
	const { atelier, fournitures } = useSelector((state) => state.atelierInfo)
	const { fournisseurs } = useSelector((state) => state.autreInfo)
	const { groups } = useSelector((state) => state.navInfo.login)
	const [showModalModifierPrixVente, setShowModalModifierPrixVente] =
		useState(false)
	const { canSeeAdmin, canSeePrixAchat } = useAtelierPermissions()
	const { isResponsableAtelier, isBoss, isAdmin } = useGroups()

	const showPaye = useMemo(() => {
		return (
			canSeeAdmin &&
			(atelierLigne.sousTraitant > 0 ||
				[
					ATELIER_LIGNE_TYPE_AUTRE,
					ATELIER_LIGNE_TYPE_FOURNITURE,
				].includes(atelierLigne.type))
		)
	}, [canSeeAdmin, atelierLigne])

	const getFourniture = useMemo(() => {
		return fournitures.find((four) => four.id === atelierLigne.fourniture)
	}, [atelierLigne.fourniture, fournitures])

	const getSousTotal = useMemo(() => {
		const { quantite, prixVente, isHT } = atelierLigne
		if (Number(quantite) && Number(prixVente)) {
			if (isHT) return quantite * prixVente
			return quantite * Addons.TTCtoHT(prixVente, atelier.tva)
		}
		return 0
	}, [
		atelierLigne,
		atelier.tva,
		atelierLigne.quantite,
		atelierLigne.prixVente,
		atelierLigne.isHT,
	])

	const getSousTotalRabais = useMemo(() => {
		const { rabais, rabaisIsFlat, quantite } = atelierLigne
		if (isModele) return getSousTotal
		if (rabaisIsFlat) return getSousTotal - rabais * quantite
		return getSousTotal * (1 - rabais / 100)
	}, [atelierLigne, getSousTotal, isModele])

	const getSousTotalAchat = useMemo(() => {
		// isAchHT (flag achat), pas isHT (flag vente) : après débit le prix
		// d'achat est HT (isAchHT=true) alors qu'une ligne article a isHT=false
		// — l'ancien test divisait le sous-total achat une fois de trop.
		let { quantite, prixAchat, isAchHT } = atelierLigne
		if (Number(quantite) && Number(prixAchat)) {
			if (isAchHT) return quantite * prixAchat
			else return quantite * Addons.TTCtoHT(prixAchat, atelier.tva)
		} else return 0
	}, [atelier.tva, atelierLigne])

	const toggleTaxe = () => {
		let newPrixVente
		// HT => TTC
		if (atelierLigne.isHT)
			newPrixVente = Addons.HTtoTTC(atelierLigne.prixVente, atelier.tva)
		// TTC => HT
		else newPrixVente = Addons.TTCtoHT(atelierLigne.prixVente, atelier.tva)
		atelierLigne.prixVente = newPrixVente
		atelierLigne.isHT = !atelierLigne.isHT
		updateAtelierLignesFull(atelierLigne, index)
	}

	const toggleAchatTaxe = () => {
		let newPrixAchat
		// HT => TTC
		if (atelierLigne.isAchHT)
			newPrixAchat = Addons.HTtoTTC(atelierLigne.prixAchat, atelier.tva)
		// TTC => HT
		else newPrixAchat = Addons.TTCtoHT(atelierLigne.prixAchat, atelier.tva)
		atelierLigne.prixAchat = newPrixAchat
		atelierLigne.isAchHT = !atelierLigne.isAchHT
		updateAtelierLignesFull(atelierLigne, index)
	}

	const toggleRabais = () => {
		let newRabais
		if (atelierLigne.rabaisIsFlat)
			newRabais = (atelierLigne.rabais / getSousTotal) * 100
		else newRabais = (getSousTotal * atelierLigne.rabais) / 100
		atelierLigne.rabais = Addons.round100(newRabais)
		atelierLigne.rabaisIsFlat = !atelierLigne.rabaisIsFlat
		updateAtelierLignesFull(atelierLigne, index)
	}

	const updatePrixVente = (e) => {
		const al = Object.assign({}, atelierLigne)
		al.prixVente = e.target.value
		if (atelierLigne.fourniture && atelierLigne.fourniture !== 0) {
			let four = getFourniture
			let prixAchat = 0
			if (e.target.value !== "") {
				if (four.operation === 0) {
					prixAchat = parseFloat(e.target.value) - four.valeur
				} else {
					prixAchat = Addons.round100(
						(parseFloat(e.target.value) * (100 - four.valeur)) /
							100,
					)
				}
			}
			al.prixAchat = prixAchat
		}
		updateAtelierLignesFull(al, index)
	}

	const nextRef = (field, e, nextRef) => {
		switch (field) {
			case "nextRef":
				switch (e.keyCode) {
					case 13:
						nextRef.current.focus()
						break
					default:
						break
				}
				break
			default:
				break
		}
	}

	const checkKey = (e) => {
		if (e.key === "+") {
			e.preventDefault()
			toggleTaxe()
		} else if (e.key === "Enter") {
			setTimeout(() => {
				focusMenu(atelierLigne.type)
			}, 200)
		}
	}

	const goBack = (e) => {
		if (e.key === "Enter") {
			setTimeout(() => {
				focusMenu(atelierLigne.type)
			}, 200)
		}
	}
	const getSousTraitant = useMemo(() => {
		return fournisseurs.find((f) => f.id === atelierLigne.sousTraitant)
	}, [atelierLigne.sousTraitant, fournisseurs])
	return (
		<>
			<ModalModifierPrixVenteArticleVirtuel
				visible={showModalModifierPrixVente}
				onClose={() => setShowModalModifierPrixVente(false)}
				atelierLigne={atelierLigne}
			/>
			<div className="row full sb">
				<div
					className="dot-outter"
					style={
						index === 0 ? { height: "77px" } : { height: " 50px" }
					}
				>
					<div
						className="dot-inner"
						id={`${idname}${atelierLigne.id}`}
						data-al={idname}
						draggable={true}
						onDragStart={(e) => dragAL(e, atelierLigne.id)}
						onDragOver={(e) => dragOverAL(e, atelierLigne.id)}
						onDrop={(e) => dropAL(e)}
					>
						<FontAwesomeIcon
							style={{ fontSize: "10px" }}
							icon={faCircle}
						/>
					</div>
				</div>
				<div
					className={`lab-in${index > 0 ? " no-label" : ""}`}
					style={{ flexGrow: 1, position: "relative" }}
				>
					{index === 0 ? <label>Label</label> : null}
					{atelierLigne.article > 0 ? (
						<input
							className="left"
							value={atelierLigne.label}
							onChange={(e) =>
								updateAtelierLignes(
									e.target.value,
									"label",
									atelierLigne.id,
								)
							}
							disabled
						/>
					) : (
						<input
							className="left"
							value={atelierLigne.label}
							ref={lastRef}
							onChange={(e) =>
								updateAtelierLignes(
									e.target.value,
									"label",
									atelierLigne.id,
								)
							}
							onKeyUp={(e) => nextRef("nextRef", e, refQuantite)}
							disabled={
								atelierLigne.fourniturePaid || disableLigne
							}
							// onFocus={e => e.target.select()}
						/>
					)}
					{atelierLigne.sousTraitant > 0 && (
						<AppBtnHover
							title={getSousTraitant.label}
							tel={getSousTraitant.telephone}
							email={getSousTraitant.email}
						/>
						// <button
						// 	className={"norm-btn2 disable-hover"}
						// 	style={{
						// 		position: "absolute",
						// 		right: 5,
						// 		bottom: 5,
						// 		height: 34,
						// 		width: 50,
						// 		minWidth: 50,
						// 	}}
						// 	title={
						// 		fournisseurs.find(
						// 			(f) => f.id === atelierLigne.sousTraitant,
						// 		).label
						// 	}
						// >
						// 	<FontAwesomeIcon icon={faInfo} />
						// </button>
					)}
				</div>
				{atelierLigne.articleVirtuel > 0 && (
					<div
						className={`lab-in smedium${index > 0 ? " no-label" : ""}`}
					>
						{index === 0 && <label>Numéro</label>}
						<input
							value={atelierLigne.articleVirtuelNumero}
							disabled
						/>
					</div>
				)}
				{isResponsableAtelier && !isModele && (
					<div
						className={`lab-in${index > 0 ? " no-label" : ""}`}
						style={{ minWidth: "54px" }}
					>
						{index === 0 && <label>Validé</label>}
						<input
							type="checkbox"
							checked={atelierLigne.valide}
							onChange={(_) =>
								updateAtelierLignes(
									!atelierLigne.valide,
									"valide",
									atelierLigne.id,
								)
							}
							disabled={disableLigne}
						/>
					</div>
				)}
				<div className={`lab-in small${index > 0 ? " no-label" : ""}`}>
					{index === 0 ? <label>Quantité</label> : null}
					<input
						value={atelierLigne.quantite}
						ref={atelierLigne.article > 0 ? lastRef : refQuantite}
						onChange={(e) =>
							updateAtelierLignes(
								e.target.value,
								"quantite",
								atelierLigne.id,
							)
						}
						onKeyUp={
							atelierLigne.article > 0
								? (e) => goBack(e)
								: atelierLigne.fourniture > 0
									? (e) => nextRef("nextRef", e, refPrixU)
									: groups.includes("responsable_atelier")
										? (e) =>
												nextRef(
													"nextRef",
													e,
													refPrixAchat,
												)
										: (e) => nextRef("nextRef", e, refPrixU)
						}
						onKeyDown={(e) => Addons.typeNumber(e)}
						onFocus={(e) => e.target.select()}
						disabled={atelierLigne.fourniturePaid || disableLigne}
					/>
				</div>
				{canSeePrixAchat ? (
					<>
						<div
							className={`lab-in small${
								index > 0 ? " no-label" : ""
							}`}
						>
							{index === 0 ? <label>Prix achat</label> : null}
							<input
								value={atelierLigne.prixAchat}
								ref={refPrixAchat}
								onChange={(e) =>
									updateAtelierLignes(
										e.target.value,
										"prixAchat",
										atelierLigne.id,
									)
								}
								onKeyUp={
									atelierLigne.article > 0
										? (e) => goBack(e)
										: (e) => nextRef("nextRef", e, refPrixU)
								}
								onKeyDown={(e) => Addons.typeNumber(e)}
								onFocus={(e) => e.target.select()}
								disabled={
									atelierLigne.article > 0 ||
									atelierLigne.articleVirtuel > 0 ||
									atelierLigne.fourniturePaid ||
									disableLigne ||
									atelierLigne.fourniture > 0
								}
							/>
						</div>
						<button
							type="button"
							className={`norm-btn2 small${
								atelierLigne.isAchHT ? "" : " clicked"
							}${index === 0 ? " no-label" : ""}`}
							onClick={() => toggleAchatTaxe()}
							disabled={
								atelierLigne.fourniturePaid || disableLigne
							}
						>
							{atelierLigne.isAchHT ? "HT" : "TTC"}
						</button>
					</>
				) : null}
				{!isSVP && (
					<>
						<div
							className={`lab-in small${
								index > 0 ? " no-label" : ""
							}${atelierLigne.prixVenteModifie ? " important" : ""}`}
						>
							{index === 0 ? <label>Prix vente</label> : null}
							{atelierLigne.article > 0 ? (
								<input
									value={Addons.round100(
										atelierLigne.prixVente,
									)}
									readOnly
									disabled
								/>
							) : (
								<input
									className={`${atelierLigne.prixVenteModifie ? " important" : ""}`}
									value={atelierLigne.prixVente}
									ref={refPrixU}
									onChange={(e) => updatePrixVente(e)}
									onKeyDown={(e) =>
										e.key === "Enter" || e.key === "+"
											? checkKey(e)
											: Addons.typeNumber(e)
									}
									onFocus={(e) => e.target.select()}
									readOnly={
										atelierLigne.articleVirtuel > 0 ||
										atelierLigne.fourniturePaid ||
										disableLigne
									}
								/>
							)}
						</div>
						<button
							type="button"
							className={`norm-btn2 small${
								atelierLigne.isHT ? "" : " clicked"
							}${index === 0 ? " no-label" : ""}`}
							onClick={() => toggleTaxe(index)}
							disabled={
								atelierLigne.fourniturePaid || disableLigne
							}
						>
							{atelierLigne.isHT ? "HT" : "TTC"}
						</button>
					</>
				)}
				{!isModele && !isSVP && (
					<>
						<div
							className={`lab-in small${index > 0 ? " no-label" : ""}`}
						>
							{index === 0 ? <label>{`Rabais`}</label> : null}
							<input
								value={atelierLigne.rabais}
								onChange={(e) =>
									updateAtelierLignes(
										e.target.value,
										"rabais",
										atelierLigne.id,
									)
								}
								onKeyDown={(e) => Addons.typeNumber(e)}
								onFocus={(e) => e.target.select()}
								disabled={
									atelierLigne.fourniturePaid || disableLigne
								}
							/>
						</div>
						<button
							type="button"
							className={`norm-btn2 small${
								atelierLigne.rabaisIsFlat ? "" : " clicked"
							}${index === 0 ? " no-label" : ""}`}
							onClick={() => toggleRabais(index)}
							disabled={
								atelierLigne.fourniturePaid || disableLigne
							}
						>
							{atelierLigne.rabaisIsFlat ? "HT" : "%"}
						</button>
					</>
				)}
				<div className={`lab-in small${index > 0 ? " no-label" : ""}`}>
					{index === 0 ? <label>Sous-total HT</label> : null}
					<input
						value={Addons.round100(
							isSVP ? getSousTotalAchat : getSousTotalRabais,
						)}
						disabled
					/>
				</div>
				{atelier.isAssurance && !isSVP && !isModele && (
					<div className={`lab-in${index > 0 ? " no-label" : ""}`}>
						{index === 0 ? <label>Payé par</label> : null}
						<select
							value={atelierLigne.payePar}
							onChange={(e) =>
								updateAtelierLignes(
									e.target.value,
									"payePar",
									atelierLigne.id,
								)
							}
							disabled={
								atelierLigne.fourniturePaid || disableLigne
							}
						>
							<option value={PAYE_PAR_ASSURANCE}>
								Assurance
							</option>
							<option value={PAYE_PAR_CLIENT}>Client</option>
							<option value={PAYE_PAR_MASTER}>Perte</option>
						</select>
					</div>
				)}
				{!isModele &&
					(showPaye ? (
						<div
							className={`lab-in${index > 0 ? " no-label" : ""}`}
							style={{ width: "54px" }}
						>
							{index === 0 ? <label>Payé</label> : null}
							<input
								type="checkbox"
								checked={atelierLigne.fourniturePaid}
								onChange={(_) =>
									updateAtelierLignes(
										!atelierLigne.fourniturePaid,
										"fourniturePaid",
										atelierLigne.id,
									)
								}
								disabled={!(isBoss || isAdmin)}
							/>
						</div>
					) : (
						<div style={{ width: "64px" }} />
					))}
				<div
					className={`lab-in no-label${index === 0 ? " pt-34" : ""}`}
				>
					<button
						type="button"
						className="norm-btn colored"
						onClick={() => setShowModalModifierPrixVente(true)}
						disabled={
							(!atelier.isAssurance &&
								atelierLigne.article === 0 &&
								atelierLigne.articleVirtuel === 0) ||
							disableLigne ||
							!Number.isInteger(atelier.id) ||
							!Number.isInteger(atelierLigne.id)
						}
					>
						<FontAwesomeIcon icon={faDollarSign} />
					</button>
				</div>
				<div
					className={`lab-in no-label${index === 0 ? " pt-34" : ""}`}
				>
					<button
						type="button"
						className="norm-btn colored red"
						onClick={() => removeAtelierLignes(atelierLigne)}
						disabled={disableLigne}
					>
						<FontAwesomeIcon icon={faTrash} />
					</button>
				</div>
			</div>
		</>
	)
}

const ModalModifierPrixVenteArticleVirtuel = ({
	onClose,
	visible,
	atelierLigne,
}) => {
	const dispatch = useDispatch()
	const { atelierLignes, atelier } = useSelector((state) => state.atelierInfo)
	const [menu, setMenu] = useState(atelier.isAssurance ? 0 : 1)
	const [pourcentageParticipation, setPourcentageParticipation] =
		useState("50")
	const [nouveauPrixVente, setNouveauPrixVente] = useState("")
	const modifier = () => {
		let tempAL = Object.assign({}, atelierLigne)
		let participationAL = Object.assign({}, tempAL)
		let ALtoAdd = []
		tempAL.prixVenteModifie = true
		switch (menu) {
			case 0:
				participationAL.id = Math.random()
				participationAL.label = `Participation client ${pourcentageParticipation}%: ${tempAL.label}`
				participationAL.prixVente = round100(
					tempAL.prixVente * (pourcentageParticipation / 100),
				)
				participationAL.prixAchat = 0
				participationAL.articleVirtuel = 0
				participationAL.payePar = PAYE_PAR_CLIENT
				participationAL.prixVenteModifie = false
				participationAL.dupliquer_from = tempAL.id
				tempAL.prixVenteFirstValue = tempAL.prixVente
				tempAL.prixVente = round100(
					tempAL.prixVente * ((100 - pourcentageParticipation) / 100),
				)
				ALtoAdd.push(tempAL)
				ALtoAdd.push(participationAL)
				break
			case 1:
				tempAL.prixVente = nouveauPrixVente
				ALtoAdd.push(tempAL)
				break
			default:
				break
		}
		let indexAL = atelierLignes.findIndex((al) => al.id === tempAL.id)
		let tempALS = [
			...atelierLignes.slice(0, indexAL),
			...ALtoAdd,
			...atelierLignes.slice(indexAL + 1),
		]
		dispatch({
			type: "UPDATE_ATELIERLIGNES",
			value: tempALS,
		})
		onClose()
	}
	return (
		<Modal
			center={true}
			focusTrapped={false}
			open={visible}
			onClose={onClose}
			showCloseIcon={false}
			classNames={{ overlay: "overlay", modal: "modal" }}
		>
			<div className="col flex-center">
				<h2>Modifier</h2>
				<div className="row">
					{atelier.isAssurance && (
						<button
							className={`norm-btn3 ${menu === 0 ? "clicked" : ""}`}
							onClick={() => setMenu(0)}
						>
							Dupliquer ligne
						</button>
					)}
					<button
						className={`norm-btn3 ${menu === 1 ? "clicked" : ""}`}
						onClick={() => setMenu(1)}
					>
						Prix de vente
					</button>
				</div>
				{menu === 0 && (
					<div className={"lab-in"}>
						<label>Pourcentage de participation</label>
						<input
							value={pourcentageParticipation}
							onChange={(e) =>
								setPourcentageParticipation(e.target.value)
							}
							onKeyDown={(e) => typeNumber(e)}
						/>
					</div>
				)}
				{menu === 1 && (
					<div className={"lab-in"}>
						<label>Nouveau prix de vente</label>
						<input
							value={nouveauPrixVente}
							onChange={(e) =>
								setNouveauPrixVente(e.target.value)
							}
							onKeyDown={(e) => typeNumber(e)}
							placeholder={atelierLigne.prixVente}
						/>
					</div>
				)}
				<button className={"norm-btn2 mt-20"} onClick={modifier}>
					Modifier
				</button>
			</div>
		</Modal>
	)
}

export default AtelierLigneV2

export const getBaseAtelierLigne = (type) => {
	return {
		id: Math.random(),
		label: "",
		quantite: 0,
		prixVente: 0,
		prixVenteModifie: false,
		prixVenteFirstValue: 0,
		prixAchat: 0,
		payePar: PAYE_PAR_CLIENT,
		type: parseInt(type),
		isHT: true,
		valide: false,
		article: 0,
		fourniture: 0,
		sousTraitant: 0,
		articleVirtuel: 0,
		isAchHT: true,
		fourniturePaid: false,
		rabais: 0,
		rabaisIsFlat: true,
		dupliquer_from: null,
	}
}

export const PAYE_PAR_ASSURANCE = 0
export const PAYE_PAR_CLIENT = 1
export const PAYE_PAR_MASTER = 2
export const ATELIER_LIGNE_TYPE_FOURNITURE = 0
export const ATELIER_LIGNE_TYPE_MAIN_OEUVRE = 1
export const ATELIER_LIGNE_TYPE_AUTRE = 2
export const ATELIER_LIGNE_TYPE_SUPPLEMENTAIRE = 3
